import React, { useEffect, useState } from 'react';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';
import { Board } from './components/Board';
import { Box } from '@mui/material';
import { Column, Item } from './components/Board/interfaces';
import {
  useGetStages,
  useUpdateItemMutation,
} from '../../../reactQuery/opportunities';
import { enqueueSnackbar } from 'notistack';
import { FloatingActionsButtons } from '../../../components/FloatingActionsButtons';
import { CreateAction } from '../../../components/FloatingActionsButtons/components/CreateAction';
import { UpsertItemDialog } from '../dialogs/UpsertItemDialog';
import { DeleteItemDialog } from '../dialogs/DeleteItemDialog';
import { FilterOnAgentsButton } from './components/FilterOnAgentsButton';
import { Item as ApiItem } from '../../../api/opportunitiesApi/interfaces';
import { useAuth } from '../../../context/AuthProvider';
import {
  readObjectFromLocalStorage,
  storeDataInLocalStorage,
} from '../../../helpers/localStorageHelper';
import { STORAGE_KEYS } from '../../../constants';
import { CloseItemDialog } from '../dialogs/CloseItemDialog';

export const OpportunitiesDashboard: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const [columns, setColumns] = useState<Column[]>([]);
  const [showUpsertDialog, setShowUpsertDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<ApiItem | undefined>(
    undefined
  );
  const [selectedItemToUpdate, setSelectedItemToUpdate] = useState<
    ApiItem | undefined
  >(undefined);
  const [selectedItemToClose, setSelectedItemToClose] = useState<
    ApiItem | undefined
  >(undefined);
  const [filterOnAgents, setFilterOnAgents] = useState<number[]>(() => {
    const agentsFilterSetting = readObjectFromLocalStorage(
      STORAGE_KEYS.opportunitiesOverviewAgentFilter
    );

    if (agentsFilterSetting) {
      return agentsFilterSetting as number[];
    }

    return [];
  });
  const isAdmin = auth?.user && auth.user.isAdmin === true;
  const currentAgentId = auth?.user ? auth.user.id : null;

  const { data: stages } = useGetStages({
    includeItems: true,
    filters: [
      {
        field: 'agent',
        values: filterOnAgents,
      },
    ],
  });

  const { mutateAsync: updateItem, isLoading: isUpdatingItem } =
    useUpdateItemMutation();

  const getItemWithId = (itemId: number): ApiItem | undefined => {
    for (const stage of stages || []) {
      if (!stage.items) continue;

      const found = stage.items.find((item) => item.id === itemId);

      if (found) {
        return found;
      }
    }

    return undefined;
  };

  useEffect(() => {
    if (!stages) {
      return;
    }

    setColumns(
      stages.map((stage) => {
        const items: Item[] | undefined = stage.items?.map((item) => {
          return {
            id: item.id,
            title: item.title,
            product: item.product.name,
            contact: `${item.contact.firstName} ${item.contact.lastName}`,
            agent: {
              firstName: item.agent.firstName,
              lastName: item.agent.lastName,
              avatar: item.agent.avatar,
            },
            client: item.client.name,
            canCloseOpportunity: isAdmin || item.agent.id === currentAgentId,
          };
        });

        return {
          id: stage.id,
          title: stage.name,
          color: stage.color,
          items: items || [],
        };
      })
    );
  }, [stages]);

  useEffect(() => {
    storeDataInLocalStorage(
      STORAGE_KEYS.opportunitiesOverviewAgentFilter,
      filterOnAgents
    );
  }, [filterOnAgents]);

  const handleItemColumnChange = async (
    itemId: number | string,
    newColumnId: string | number
  ) => {
    console.log(
      `Item with ID ${itemId} is changed to column with ID ${newColumnId}`
    );
    try {
      await updateItem({
        itemId: itemId as number,
        itemData: {
          stageId: newColumnId as number,
        },
      });

      return true;
    } catch (error) {
      enqueueSnackbar(
        t('opportunities:dashboard.errors.failedToChangeItemStage'),
        {
          variant: 'error',
        }
      );

      return false;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 30px)',
      }}
    >
      <PageTitle
        title={t('opportunities:dashboard.title')}
        subTitle={t('opportunities:dashboard.subTitle')}
        space
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
        }}
      >
        {isAdmin && (
          <FilterOnAgentsButton
            selectedAgents={filterOnAgents}
            onChange={(selectedAgents) => setFilterOnAgents(selectedAgents)}
          />
        )}
      </PageTitle>

      <Board
        columns={columns}
        setColumns={setColumns}
        handleItemColumnChange={handleItemColumnChange}
        handleItemDeletion={(item: Item) => {
          setSelectedItem(getItemWithId(item.id as number));

          setShowDeleteDialog(true);
        }}
        handleItemEdit={(itemId: number | string) => {
          setSelectedItemToUpdate(getItemWithId(itemId as number));

          setShowUpsertDialog(true);
        }}
        handleItemClose={(item) => {
          setSelectedItemToClose(getItemWithId(item.id as number));
        }}
      />

      <UpsertItemDialog
        show={showUpsertDialog}
        item={selectedItemToUpdate}
        onClose={() => {
          setShowUpsertDialog(false);
          setSelectedItemToUpdate(undefined);
        }}
      />

      {selectedItem && (
        <DeleteItemDialog
          show={showDeleteDialog}
          item={selectedItem}
          onClose={() => {
            setShowDeleteDialog(false);
            setSelectedItem(undefined);
          }}
        />
      )}

      {selectedItemToClose && (
        <CloseItemDialog
          show={true}
          item={selectedItemToClose}
          onClose={() => {
            setSelectedItemToClose(undefined);
          }}
        />
      )}

      <FloatingActionsButtons>
        <CreateAction onClick={() => setShowUpsertDialog(true)} />
      </FloatingActionsButtons>
    </Box>
  );
};
